export type ProjectStatusCode = "new" | "production" | "produced" | string;
export type EditorMode = "plan" | "front" | "3d";
export type ToolId =
  | "select"
  | "wall"
  | "door"
  | "window"
  | "measure"
  | "geometry"
  | "stair"
  | "woodwork"
  /** Draws a rectangle, circle or polygon and pulls it into a part (17.0). */
  | "draw-part"
  /**
   * Executa a ação que o componente declara para o clique — abrir a porta,
   * correr a gaveta. É a mãozinha da referência.
   */
  | "interact";

export type Vec2 = [number, number];
export type Vec3 = [number, number, number];
export type Quaternion = [number, number, number, number];

export interface Point2D {
  x: number;
  z: number;
}

export interface Transform3D {
  translationMm: Vec3;
  rotationRad: Vec3;
  rotationQuaternion?: Quaternion;
  scale: Vec3;
}

export interface MaterialRef {
  materialId: string;
  revision: string;
  overrides?: Record<string, unknown>;
}

export interface PbrMaterial {
  id: string;
  revision: string;
  name: string;
  category: string;
  baseColorFactor: [number, number, number, number];
  roughness: number;
  metallic: number;
  opacity: number;
  textureRefs: {
    baseColor?: string;
    normal?: string;
    roughness?: string;
    metallic?: string;
    opacity?: string;
    emissive?: string;
    ambientOcclusion?: string;
  };
  mapping: {
    mode: "uv" | "planar" | "box" | "triplanar";
    physicalSizeMm?: Vec2;
    scale: Vec2;
    rotationRad: number;
    offset: Vec2;
  };
}

export interface Opening {
  id: string;
  revision: number;
  kind: "door" | "window";
  wallId: string;
  catalogRef?: {
    itemId: string;
    revision: string;
  };
  offsetMm: number;
  widthMm: number;
  heightMm: number;
  sillMm: number;
  depthMm: number;
  materialId: string;
  materialAssignments: Record<string, MaterialRef>;
  inverted: boolean;
  opening: {
    kind: "swing" | "tilt" | "slide" | "fixed";
    progress: number;
    angleRad?: number;
  };
  extensions: Record<string, unknown>;
}

export interface Wall {
  id: string;
  kind: "wall";
  revision: number;
  levelId: string;
  layerId: string;
  startMm: Vec2;
  endMm: Vec2;
  thicknessMm: number;
  heightMm: number;
  baseline: "center" | "left" | "right";
  materialId: string;
  materialAssignments: Record<string, MaterialRef>;
  locked: boolean;
  openingIds: string[];
  /** Legacy mirror kept while migrating scenes created before schema 2. */
  openings?: Opening[];
  extensions: Record<string, unknown>;
}

export interface Room {
  id: string;
  levelId: string;
  layerId: string;
  name: string;
  boundary: Vec2[];
  holes: Vec2[][];
  areaMm2: number;
  materialAssignments: Record<string, MaterialRef>;
  valid: boolean;
  diagnostics: string[];
  extensions: Record<string, unknown>;
}

export interface SceneElement {
  id: string;
  kind: "module" | "geometry" | "stair" | "panel" | "model" | "primitive";
  revision: number;
  name: string;
  layerId: string;
  transform: Transform3D;
  dimensionsMm: {
    width: number;
    height: number;
    depth: number;
  };
  catalogRef?: {
    itemId: string;
    revision: string;
  };
  parentRef?: {
    elementId: string;
    slot?: string;
  };
  materialId: string;
  materialAssignments: Record<string, MaterialRef>;
  locked: boolean;
  visible: boolean;
  parameters: Record<string, unknown>;
  componentVisibility: Record<string, boolean>;
  instanceOverrides: Record<string, unknown>;
  operationIds: string[];
  extensions: Record<string, unknown>;
}

export interface SceneLayer {
  id: string;
  name: string;
  order: number;
  visible: boolean;
  locked: boolean;
  opacity: number;
}

export interface Measurement {
  id: string;
  kind: "linear" | "angular" | "area";
  pointsMm: Vec3[];
  scope: "analysis" | "documentation" | "manufacturing";
  label?: string;
}

export interface CurveOperation {
  id: string;
  type: "curve";
  targetElementId: string;
  innerRadiusMm: number;
  angleRad: number;
  offsetMm: number;
  alignment: "left" | "center" | "right";
  bendDirection: "up" | "down";
  kerfWidthMm: number;
  kerfSpacingMm: number;
  coverThicknessMm: number;
  remainingThicknessMm: number;
}

export interface SlatOperation {
  id: string;
  type: "slat";
  targetElementId: string;
  orientation: "vertical" | "horizontal" | "diagonal-right" | "diagonal-left";
  face: "front" | "back";
  mode: "edge" | "flat";
  sectionMm: Vec2;
  distribution:
    | { mode: "spacing"; spacingMm: number }
    | { mode: "count"; count: number };
  alignment: "start" | "center" | "end";
  offsetsMm: { top: number; right: number; bottom: number; left: number };
}

export interface CsgOperation {
  id: string;
  type: "csg";
  operation: "union" | "subtract" | "intersect";
  operands: Array<{ elementId: string; revision: number }>;
  toleranceMm: number;
  status: "draft" | "processing" | "ready" | "failed";
  resultHash?: string;
  errorCode?: string;
}

export interface MachiningOperation {
  id: string;
  type: "machining";
  kind: "contour-follow" | "pocket" | "drill" | "edge-band" | "chamfer" | "round";
  targetElementId: string;
  face: "front" | "back" | "top" | "bottom" | "edge";
  frame: "part-local";
  path?: Vec2[];
  closed?: boolean;
  widthMm?: number;
  depthMm?: number;
  toolRef?: string;
  parameters: Record<string, unknown>;
}

export type SceneOperation =
  | CurveOperation
  | SlatOperation
  | CsgOperation
  | MachiningOperation;

export interface CameraState {
  projection: "perspective" | "orthographic";
  targetMm: Vec3;
  azimuthRad: number;
  elevationRad: number;
  distanceMm: number;
  orthoSizeMm?: number;
  fovRad: number;
  nearMm: number;
  farMm: number;
}

export interface PostProcessingSettings {
  viewportQuality: "performance" | "high";
  enabled: boolean;
  exposure: number;
  contrast: number;
  saturation: number;
  vignette: number;
  bloom: number;
  chromaticAberration: number;
  depthOfField: number;
  sharpening: number;
  grain: number;
  toneMapping: "standard" | "aces";
  antialiasing: "msaa" | "fxaa" | "taa" | "none";
  ambientOcclusion: number;
  colorTemperature: number;
}

export interface SceneSettings {
  gridSizeMm: number;
  snapEnabled: boolean;
  gridVisible: boolean;
  measurementsVisible: boolean;
  ceilingVisible: boolean;
  wallLoweringEnabled: boolean;
  appearance: "realistic" | "fill" | "lines";
  xrayEnabled: boolean;
  backEdgesEnabled: boolean;
  defaultWallThicknessMm: number;
  defaultWallHeightMm: number;
  angularSnapDeg: number;
  showSnapGuides: boolean;
  lighting: {
    preset: "studio" | "daylight" | "warm" | "neutral";
    intensity: number;
    orientationDeg: number;
    environmentUrl?: string;
  };
  postProcessing: PostProcessingSettings;
}

export interface SceneDocument {
  schemaVersion: 2;
  documentId: string;
  revision: number;
  units: "mm";
  coordinateSystem: "right-handed-y-up";
  catalogRevision: string;
  configuratorRevisionId: string | null;
  metadata: {
    name: string;
    locale: "pt-BR" | "en" | "es";
    createdAt: string;
    updatedAt: string;
  };
  settings: SceneSettings;
  levels: Array<{
    id: string;
    name: string;
    elevationMm: number;
    heightMm: number;
  }>;
  walls: Wall[];
  openings: Opening[];
  rooms: Room[];
  elements: SceneElement[];
  layers: SceneLayer[];
  measurements: Measurement[];
  operations: SceneOperation[];
  materials: PbrMaterial[];
  materialAssignments: Record<string, string>;
  floorProperties: Record<string, unknown>;
  surveySheet: Record<string, unknown>;
  extensions: Record<string, unknown>;
}

export interface ProjectSummary {
  id: string;
  name: string;
  statusId: string;
  statusCode: ProjectStatusCode;
  statusName: string;
  statusColor: string;
  currentRevision: number;
  updatedAt: string;
  archivedAt: string | null;
  deletedAt: string | null;
  thumbnail?: string | null;
  roomCount: number;
  areaM2: number;
  information?: Record<string, unknown>;
}

export interface ResizeAxis {
  enabled: boolean;
  /**
   * A bound only exists when the component really declares one. SKP dynamic
   * components carry no minimum or maximum for their length axes, so an import
   * leaves these null and the axis is limited by the room around it — walls,
   * ceiling and neighbours — rather than by a number invented at import time.
   */
  minMm: number | null;
  maxMm: number | null;
  stepMm: number;
  presetsMm?: number[];
}

/**
 * What a catalogue category holds. Modules and materials share one tree — the
 * same folders, the same drag and drop, the same search — because they are the
 * same kind of thing to whoever is organising the library. What differs is what
 * "add" means inside each: a module comes from an SKP with geometry, a material
 * is a colour or a texture. The kind is what lets one screen offer both without
 * asking every time.
 *
 * A subfolder never disagrees with its root: the kind is inherited on creation.
 */
export type CatalogFolderKind = "module" | "material";

export interface CatalogFolder {
  id: string;
  name: string;
  parentId: string | null;
  path: string[];
  sortOrder: number;
  active: boolean;
  /** Absent on folders created before the field existed; they hold modules. */
  kind?: CatalogFolderKind;
}

export interface CatalogComponentNode {
  id: string;
  name: string;
  path: string;
  definitionName?: string;
  layer?: string;
  properties?: Record<string, string>;
  children: CatalogComponentNode[];
}

export interface CatalogEngineering {
  sourceAssetId: string;
  renderAssetId: string;
  metadataAssetId?: string;
  thumbnailAssetId?: string;
  sourceFileName: string;
  sourceVersion: string;
  componentCount: number;
  meshCount: number;
  extractionMethod?: "sketchup-c-sdk" | "glb-native";
  sdkApiVersion?: string;
  sourceNewerThanSdk?: boolean;
  definitionCount?: number;
  instanceCount?: number;
  dictionaryCount?: number;
  dynamicDictionaryCount?: number;
  attributeValueCount?: number;
  unavailableValueCount?: number;
  formulaCount?: number;
  root: CatalogComponentNode;
  meshBindings: Array<{
    meshName: string;
    componentPath: string;
  }>;
  /**
   * The materials the SketchUp file declared, lifted out of the mesh.
   *
   * The render asset already draws with them, baked in. Keeping the list here
   * as well is what makes them *materials* rather than pixels: the editor can
   * offer them in the palette, put one on another module, and show or replace
   * the texture. Each texture image is stored as its own asset so a module with
   * twenty materials does not carry twenty images in its document.
   */
  materials?: Array<{
    /** Stable within the source file. */
    key: string;
    name: string;
    colorHex: string;
    opacity: number;
    /** SketchUp tints the texture with the colour instead of replacing it. */
    colorized: boolean;
    texture?: {
      assetId: string;
      fileName: string;
      mimeType: string;
      /** Size of one tile on the face, in millimetres. */
      widthMm: number;
      heightMm: number;
    };
  }>;
  /**
   * Asset holding the SketchUp dynamic-component graph, when the module has
   * one. Kept out of the item document because a graph runs from 60 KB to a
   * few hundred, and only the editor needs it — and only once the module is
   * actually placed.
   */
  dynamicAssetId?: string;
  /** What the extraction found, or why there is no graph. */
  dynamic?:
    | {
        available: true;
        nodeCount: number;
        dynamicNodeCount: number;
        formulaCount: number;
        copiesNodeCount: number;
        ambiguousPathCount: number;
        partCountAtImport: number;
        problemCount: number;
      }
    | { available: false; reason: string };
}

export interface CatalogItem {
  id: string;
  stableItemId: string;
  revision?: string;
  name: string;
  description?: string;
  folderId?: string;
  /**
   * Where the module sits among the others in its folder. The catalogue admin
   * screen writes it by drag and drop and the module carousel inside a project
   * reads it, so both show the same order.
   */
  sortOrder?: number;
  thumbnailAssetId?: string;
  /** Reduced derivative used by compact catalogue cards. */
  cardThumbnailAssetId?: string;
  /** Generation recipe used by the compact thumbnail, for migrations. */
  cardThumbnailVersion?: number;
  category: string;
  root: string;
  type: "architecture" | "module" | "material";
  dimensionsMm: {
    width: number;
    height: number;
    depth: number;
  };
  color: string;
  pbr?: Partial<PbrMaterial>;
  resize?: {
    width: ResizeAxis;
    height: ResizeAxis;
    depth: ResizeAxis;
    preserveAspect?: boolean;
    /**
     * "dynamic" re-evaluates the SketchUp dynamic-component formulas on every
     * resize, so each part takes its own new size; "scale" stretches the whole
     * baked block, which thickens panels that should stay 15 mm.
     */
    regenerationPolicy?: "scale" | "parametric" | "fixed" | "dynamic";
  };
  parameterDefaults?: Record<string, number | string | boolean>;
  formulas?: Record<string, string>;
  components?: Array<Record<string, unknown>>;
  engineering?: CatalogEngineering;
  manufacturing?: Record<string, unknown>;
  licenseStatus: "clean_room" | "confirmed" | "unknown";
  status: "draft" | "published" | "retired" | "quarantined";
  aliases: string[];
  availability?: "available" | "empty";
}

export interface SceneCommand<TPayload = unknown> {
  commandId: string;
  documentId: string;
  expectedRevision: number;
  actorId: string;
  type: string;
  payload: TPayload;
  createdAt: string;
}

export const defaultLayer: SceneLayer = {
  id: "layer-architecture",
  name: "Arquitetura",
  order: 0,
  visible: true,
  locked: false,
  opacity: 1,
};

// Slightly darker than the catalog's Porcelanato Concreto (#B8BDC4), so the
// default floor remains distinct from white walls under daylight lighting.
export const DEFAULT_FLOOR_COLOR = "#ACB2BA";

export const defaultPostProcessing: PostProcessingSettings = {
  viewportQuality: "performance",
  enabled: true,
  exposure: 1,
  contrast: 1,
  saturation: 1,
  vignette: 0.08,
  bloom: 0.08,
  chromaticAberration: 0,
  depthOfField: 0,
  sharpening: 0.15,
  grain: 0,
  toneMapping: "aces",
  antialiasing: "msaa",
  ambientOcclusion: 0.35,
  colorTemperature: 0,
};

export function createBlankScene(name = "Nova planta", documentId = makeId("scene")): SceneDocument {
  const now = new Date().toISOString();
  return {
    schemaVersion: 2,
    documentId,
    revision: 0,
    units: "mm",
    coordinateSystem: "right-handed-y-up",
    catalogRevision: "calcplan-clean-room@1",
    configuratorRevisionId: null,
    metadata: {
      name,
      locale: "pt-BR",
      createdAt: now,
      updatedAt: now,
    },
    settings: {
      gridSizeMm: 100,
      snapEnabled: true,
      gridVisible: true,
      measurementsVisible: true,
      ceilingVisible: false,
      wallLoweringEnabled: false,
      appearance: "realistic",
      xrayEnabled: false,
      backEdgesEnabled: false,
      defaultWallThicknessMm: 150,
      defaultWallHeightMm: 2_700,
      angularSnapDeg: 15,
      showSnapGuides: true,
      lighting: {
        preset: "daylight",
        intensity: 1,
        orientationDeg: -35,
      },
      postProcessing: structuredClone(defaultPostProcessing),
    },
    levels: [{ id: "level-ground", name: "Térreo", elevationMm: 0, heightMm: 2_700 }],
    walls: [],
    openings: [],
    rooms: [],
    elements: [],
    layers: [structuredClone(defaultLayer)],
    measurements: [],
    operations: [],
    materials: [],
    materialAssignments: {},
    floorProperties: {},
    surveySheet: {},
    extensions: {},
  };
}

export function createWall(
  startMm: Vec2,
  endMm: Vec2,
  settings: Pick<SceneSettings, "defaultWallThicknessMm" | "defaultWallHeightMm">,
  layerId = defaultLayer.id,
): Wall {
  return {
    id: makeId("wall"),
    kind: "wall",
    revision: 0,
    levelId: "level-ground",
    layerId,
    startMm,
    endMm,
    thicknessMm: settings.defaultWallThicknessMm,
    heightMm: settings.defaultWallHeightMm,
    baseline: "center",
    materialId: "#FFFFFF",
    materialAssignments: {},
    locked: false,
    openingIds: [],
    extensions: {},
  };
}

export function createSquareScene(name = "Planta quadrada 10 × 10"): SceneDocument {
  const scene = createBlankScene(name);
  const points: Vec2[] = [
    [0, 0],
    [10_000, 0],
    [10_000, 10_000],
    [0, 10_000],
  ];
  scene.walls = points.map((point, index) => {
    const wall = createWall(point, points[(index + 1) % points.length]!, scene.settings);
    wall.id = `wall-${index + 1}`;
    return wall;
  });
  const opening = createOpening("door", scene.walls[1]!, 4_100);
  opening.id = "opening-door-main";
  scene.openings.push(opening);
  scene.walls[1]!.openingIds.push(opening.id);
  scene.rooms = [
    {
      id: "room-main",
      levelId: "level-ground",
      layerId: defaultLayer.id,
      name: "Ambiente 1",
      boundary: points,
      holes: [],
      areaMm2: 100_000_000,
      materialAssignments: {},
      valid: true,
      diagnostics: [],
      extensions: {},
    },
  ];
  return scene;
}

export function createOpening(kind: "door" | "window", wall: Wall, offsetMm?: number): Opening {
  const wallLength = distance2D(wall.startMm, wall.endMm);
  const widthMm = kind === "door" ? 900 : 1_200;
  return {
    id: makeId("opening"),
    revision: 0,
    kind,
    wallId: wall.id,
    offsetMm: Math.max(0, Math.min(wallLength - widthMm, offsetMm ?? (wallLength - widthMm) / 2)),
    widthMm,
    heightMm: kind === "door" ? 2_100 : 1_200,
    sillMm: kind === "door" ? 0 : 900,
    depthMm: wall.thicknessMm,
    materialId: kind === "door" ? "#8B6B4A" : "#8CD5F5",
    materialAssignments: {},
    inverted: false,
    opening: { kind: kind === "door" ? "swing" : "slide", progress: 0 },
    extensions: {},
  };
}

export function normalizeScene(input: unknown, documentId?: string): SceneDocument {
  const raw = (input && typeof input === "object" ? input : {}) as Partial<SceneDocument> & {
    schemaVersion?: number;
    walls?: Array<Partial<Wall> & { openings?: Array<Partial<Opening>> }>;
    elements?: Array<Partial<SceneElement>>;
    layers?: Array<Partial<SceneLayer>>;
    measurements?: Array<Partial<Measurement> & { startMm?: Vec3; endMm?: Vec3 }>;
  };
  const scene = createBlankScene(raw.metadata?.name ?? "Nova planta", raw.documentId ?? documentId);
  scene.revision = Number.isInteger(raw.revision) ? Number(raw.revision) : 0;
  scene.catalogRevision = raw.catalogRevision ?? scene.catalogRevision;
  scene.configuratorRevisionId = raw.configuratorRevisionId ?? null;
  scene.metadata = { ...scene.metadata, ...(raw.metadata ?? {}) };
  scene.settings = {
    ...scene.settings,
    ...(raw.settings ?? {}),
    lighting: { ...scene.settings.lighting, ...(raw.settings?.lighting ?? {}) },
    postProcessing: {
      ...scene.settings.postProcessing,
      ...(raw.settings?.postProcessing ?? {}),
    },
  };
  if (scene.settings.xrayEnabled && scene.settings.backEdgesEnabled) {
    scene.settings.backEdgesEnabled = false;
  }
  scene.levels = Array.isArray(raw.levels) && raw.levels.length ? structuredClone(raw.levels) : scene.levels;
  scene.layers = (raw.layers ?? [defaultLayer]).map((layer, index) => ({
    id: layer.id ?? makeId("layer"),
    name: layer.name ?? `Camada ${index + 1}`,
    order: layer.order ?? index,
    visible: layer.visible ?? true,
    locked: layer.locked ?? false,
    opacity: layer.opacity ?? 1,
  }));
  scene.walls = (raw.walls ?? []).map((wall) => ({
    id: wall.id ?? makeId("wall"),
    kind: "wall",
    revision: wall.revision ?? 0,
    levelId: wall.levelId ?? "level-ground",
    layerId: wall.layerId ?? scene.layers[0]!.id,
    startMm: wall.startMm ?? [0, 0],
    endMm: wall.endMm ?? [1_000, 0],
    thicknessMm: wall.thicknessMm ?? scene.settings.defaultWallThicknessMm,
    heightMm: wall.heightMm ?? scene.settings.defaultWallHeightMm,
    baseline: wall.baseline ?? "center",
    materialId: wall.materialId ?? "#FFFFFF",
    materialAssignments: wall.materialAssignments ?? {},
    locked: wall.locked ?? false,
    openingIds: wall.openingIds ?? (wall.openings ?? []).map((opening) => opening.id ?? makeId("opening")),
    extensions: wall.extensions ?? {},
  }));
  const embeddedOpenings = (raw.walls ?? []).flatMap((wall) =>
    (wall.openings ?? []).map((opening, index) => ({
      ...opening,
      id: opening.id ?? wall.openingIds?.[index] ?? makeId("opening"),
      wallId: wall.id ?? "",
    })),
  );
  scene.openings = [...(raw.openings ?? []), ...embeddedOpenings]
    .filter((opening, index, all) => all.findIndex((candidate) => candidate.id === opening.id) === index)
    .map((opening) => {
      const wall = scene.walls.find((candidate) => candidate.id === opening.wallId) ?? scene.walls[0];
      const fallback = wall ? createOpening(opening.kind ?? "door", wall, opening.offsetMm) : null;
      return {
        id: opening.id ?? fallback?.id ?? makeId("opening"),
        revision: opening.revision ?? 0,
        kind: opening.kind ?? "door",
        wallId: opening.wallId ?? wall?.id ?? "",
        catalogRef: opening.catalogRef,
        offsetMm: opening.offsetMm ?? fallback?.offsetMm ?? 0,
        widthMm: opening.widthMm ?? fallback?.widthMm ?? 900,
        heightMm: opening.heightMm ?? fallback?.heightMm ?? 2_100,
        sillMm: opening.sillMm ?? fallback?.sillMm ?? 0,
        depthMm: opening.depthMm ?? wall?.thicknessMm ?? 150,
        materialId: opening.materialId ?? fallback?.materialId ?? (opening.kind === "door" ? "#8B6B4A" : "#8CD5F5"),
        materialAssignments: opening.materialAssignments ?? {},
        inverted: opening.inverted ?? false,
        opening: opening.opening ?? fallback?.opening ?? { kind: "swing", progress: 0 },
        extensions: opening.extensions ?? {},
      };
    });
  for (const wall of scene.walls) {
    wall.openingIds = scene.openings.filter((opening) => opening.wallId === wall.id).map((opening) => opening.id);
  }
  scene.rooms = (raw.rooms ?? []).map((room, index) => ({
    id: room.id ?? makeId("room"),
    levelId: room.levelId ?? "level-ground",
    layerId: room.layerId ?? scene.layers[0]!.id,
    name: room.name ?? `Ambiente ${index + 1}`,
    boundary: room.boundary ?? [],
    holes: room.holes ?? [],
    areaMm2: room.areaMm2 ?? polygonAreaMm2(room.boundary ?? []),
    materialAssignments: room.materialAssignments ?? {},
    valid: room.valid ?? true,
    diagnostics: room.diagnostics ?? [],
    extensions: room.extensions ?? {},
  }));
  scene.elements = (raw.elements ?? []).map((element) => ({
    id: element.id ?? makeId("element"),
    kind: element.kind ?? "geometry",
    revision: element.revision ?? 0,
    name: element.name ?? "Objeto",
    layerId: element.layerId ?? scene.layers[0]!.id,
    transform: {
      translationMm: element.transform?.translationMm ?? [0, 0, 0],
      rotationRad: element.transform?.rotationRad ?? [0, 0, 0],
      rotationQuaternion: element.transform?.rotationQuaternion,
      scale: element.transform?.scale ?? [1, 1, 1],
    },
    dimensionsMm: element.dimensionsMm ?? { width: 100, height: 100, depth: 100 },
    catalogRef: element.catalogRef,
    parentRef: element.parentRef,
    materialId: element.materialId ?? "#B9A07C",
    materialAssignments: element.materialAssignments ?? {},
    locked: element.locked ?? false,
    visible: element.visible ?? true,
    parameters: element.parameters ?? {},
    componentVisibility: element.componentVisibility ?? {},
    instanceOverrides: element.instanceOverrides ?? {},
    operationIds: element.operationIds ?? [],
    extensions: element.extensions ?? {},
  }));
  scene.measurements = (raw.measurements ?? []).map((measurement) => ({
    ...(() => {
      const legacy = measurement as typeof measurement & { startMm?: Vec3; endMm?: Vec3 };
      return {
    id: measurement.id ?? makeId("measurement"),
    kind: measurement.kind ?? "linear",
    pointsMm:
      measurement.pointsMm ??
      (legacy.startMm && legacy.endMm
        ? [legacy.startMm, legacy.endMm]
        : [[0, 0, 0], [1_000, 0, 0]]),
    scope: measurement.scope ?? "analysis",
    label: measurement.label,
      };
    })(),
  }));
  scene.operations = structuredClone(raw.operations ?? []);
  scene.materials = structuredClone(raw.materials ?? []);
  scene.materialAssignments = structuredClone(raw.materialAssignments ?? {});
  scene.floorProperties = structuredClone(raw.floorProperties ?? {});
  scene.surveySheet = structuredClone(raw.surveySheet ?? {});
  scene.extensions = structuredClone(raw.extensions ?? {});
  return scene;
}

export function sceneAreaM2(scene: SceneDocument): number {
  const areaMm2 = scene.rooms
    .filter((room) => room.valid)
    .reduce((total, room) => total + room.areaMm2, 0);
  if (areaMm2 > 0) return Math.round((areaMm2 / 1_000_000) * 10) / 10;
  if (scene.walls.length < 3) return 0;
  return Math.round((polygonAreaMm2(scene.walls.map((wall) => wall.startMm)) / 1_000_000) * 10) / 10;
}

export function polygonAreaMm2(points: Vec2[]): number {
  if (points.length < 3) return 0;
  const doubled = points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length]!;
    return sum + point[0] * next[1] - next[0] * point[1];
  }, 0);
  return Math.abs(doubled) / 2;
}

export function snapValue(value: number, gridSizeMm: number): number {
  return Math.round(value / gridSizeMm) * gridSizeMm;
}

export function distance2D(a: Vec2, b: Vec2): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

export function makeId(prefix: string): string {
  const id =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${id}`;
}

export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(",")}]`;
  }
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, child]) => child !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, child]) => `${JSON.stringify(key)}:${canonicalJson(child)}`);
    return `{${entries.join(",")}}`;
  }
  return JSON.stringify(value);
}
